/**
 * Project-scoped Hours — per-worker rolled up from timesheets allocated to this
 * project (Accepted/Paid only). Reuses the /api/timesheets endpoint.
 */
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";

export default function ProjectHours() {
  const { project } = useOutletContext();
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("Accepted+Paid");

  async function load() {
    const { data } = await api.get("/timesheets");
    const relevant = data.filter(t => (t.entries || []).some(e => e.project_id === project.id));
    setList(relevant);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const visible = useMemo(() => list.filter(t => {
    if (status === "All") return true;
    if (status === "Accepted+Paid") return t.status === "Accepted" || t.status === "Paid";
    return t.status === status;
  }), [list, status]);

  const rows = useMemo(() => {
    const r = [];
    for (const t of visible) {
      for (const e of (t.entries || [])) {
        if (e.project_id !== project.id) continue;
        r.push({
          id: `${t.id}:${e.task_id || e.task}`, work_date: t.work_date,
          worker_name: t.worker_name, hours: e.paid_hours || e.hours || 0,
          stage: e.stage_name || "—", task: e.task || "", status: t.status,
        });
      }
    }
    return r;
  }, [visible, project.id]);

  const total = rows.reduce((s, r) => s + parseFloat(r.hours || 0), 0);

  return (
    <div className="space-y-3" data-testid="project-hours">
      <div className="card flex gap-2 items-end flex-wrap">
        <label className="text-sm">Status
          <select className="input" value={status} onChange={e=>setStatus(e.target.value)} data-testid="ph-status">
            <option>Accepted+Paid</option>
            <option>Submitted</option>
            <option>All</option>
          </select></label>
        <div className="ml-auto text-sm text-silver">Total hours: <b data-testid="ph-total">{total.toFixed(2)} h</b></div>
      </div>
      <div className="card">
        <table className="table">
          <thead><tr><th>Date</th><th>Worker</th><th>Stage</th><th>Task</th><th>Hours</th><th>Status</th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} data-testid={`ph-row-${r.id}`}>
              <td>{fmtDate(r.work_date)}</td>
              <td>{r.worker_name}</td>
              <td>{r.stage}</td>
              <td>{r.task}</td>
              <td>{r.hours}</td>
              <td><span className="badge badge-grey">{r.status}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="6" className="opacity-50">No hours yet for this project.</td></tr>}
          </tbody></table>
      </div>
    </div>
  );
}
